/**
 * ============================================================
 * PGLP | MONTE HERMOM
 * CONSTANTS: vistoria
 * ------------------------------------------------------------
 * Fonte única de verdade para:
 *   ✔ Etapas do fluxo
 *   ✔ Nomenclatura canônica dos 6 fatores NBR 14653-2
 *   ✔ Limites normativos
 *   ✔ Helpers de navegação tipada
 *
 * REGRA: Nada de números mágicos em componente.
 * ============================================================
 */

/* =========================
   ETAPAS
========================= */

export const ETAPA = Object.freeze({
  DASHBOARD:           'dashboard',
  VISTORIA_INICIO:     'vistoria_inicio',

  F01_SERVICOS:        'f01_servicos',
  F02_INFRA:           'f02_infra',
  F03_SEGURANCA:       'f03_seguranca',
  F04_ACESSIBILIDADE:  'f04_acessibilidade',
  F05_COMERCIO:        'f05_comercio',
  F06_OCUPACAO:        'f06_ocupacao',

  FOTOS:               'fotos',
  MAPA:                'mapa',
  AMOSTRAS:            'amostras',
  RESUMO:              'resumo',
  PROTOCOLO:           'protocolo',
});

/* Ordem canônica dentro da vistoria */
export const ORDEM_FATORES = Object.freeze([
  ETAPA.F01_SERVICOS,
  ETAPA.F02_INFRA,
  ETAPA.F03_SEGURANCA,
  ETAPA.F04_ACESSIBILIDADE,
  ETAPA.F05_COMERCIO,
  ETAPA.F06_OCUPACAO,
]);

/* =========================
   DEFINIÇÃO CANÔNICA DOS FATORES
========================= */

export const FATORES_CONFIG = Object.freeze({
  [ETAPA.F01_SERVICOS]: {
    codigo:   'F01',
    pasta:    'F01_SERVICOS_PUBLICOS',
    titulo:   '01. SERVIÇOS PÚBLICOS',
    descricao:'Abastecimento de água, energia, esgotamento, gás, telecomunicações, resíduos',
    nbrItem:  '8.2.1.4.2',
  },
  [ETAPA.F02_INFRA]: {
    codigo:   'F02',
    pasta:    'F02_INFRAESTRUTURA_TRANSPORTE',
    titulo:   '02. INFRAESTRUTURA E TRANSPORTE',
    descricao:'Mobilidade urbana, fluxo viário, transporte público, vias de acesso',
    nbrItem:  '8.2.1.4.2',
  },
  [ETAPA.F03_SEGURANCA]: {
    codigo:   'F03',
    pasta:    'F03_SEGURANCA_VIGILANCIA',
    titulo:   '03. SEGURANÇA E VIGILÂNCIA',
    descricao:'Policiamento, iluminação pública, criminalidade, monitoramento',
    nbrItem:  '8.2.1.4.2',
  },
  [ETAPA.F04_ACESSIBILIDADE]: {
    codigo:   'F04',
    pasta:    'F04_ACESSIBILIDADE_VIAS',
    titulo:   '04. ACESSIBILIDADE E VIAS',
    descricao:'Calçamento, topografia, adaptabilidade PNE, classificação viária',
    nbrItem:  '8.2.1.4.2',
  },
  [ETAPA.F05_COMERCIO]: {
    codigo:   'F05',
    pasta:    'F05_COMERCIO_SERVICOS',
    titulo:   '05. COMÉRCIO E SERVIÇOS',
    descricao:'Densidade de varejo, mix de atividades, bancos, estacionamento',
    nbrItem:  '8.2.1.4.2',
  },
  [ETAPA.F06_OCUPACAO]: {
    codigo:   'F06',
    pasta:    'F06_OCUPACAO_URBANA',
    titulo:   '06. PADRÃO DE OCUPAÇÃO',
    descricao:'Tipologia predominante, verticalização, uso, tendência de mercado',
    nbrItem:  '8.2.1.4.2',
  },
});

/* =========================
   STATUS DE REVISÃO
========================= */

export const STATUS_REVISAO = Object.freeze({
  PENDENTE: 'PENDENTE',
  REVISADO: 'REVISADO',
  BLOQUEADO:'BLOQUEADO',
});

/* =========================
   LIMITES NORMATIVOS
========================= */

export const LIMITES = Object.freeze({
  // Arquivos
  ARQUIVO_MAX_MB:          20,
  ARQUIVO_MIN_MB_FOTO:     0.2,
  MEGAPIXELS_MIN:          1.2,

  // Fator de valorização
  FATOR_MIN:               0.5,
  FATOR_MAX:               1.5,
  DESVIO_JUSTIFICATIVA_PCT:15,
  DESVIO_BLOQUEIO_PCT:     30,

  // Comunicação pré-diligência
  TENTATIVAS_MINIMAS:      3,
  HORAS_ENTRE_TENTATIVAS:  24,
});

/* =========================
   HELPERS DE NAVEGAÇÃO
========================= */

/**
 * Dado o fator atual, retorna o próximo.
 * Se for o último, retorna RESUMO.
 */
export function proximaEtapa(atual) {
  const idx = ORDEM_FATORES.indexOf(atual);
  if (idx === -1) return ORDEM_FATORES[0];
  if (idx === ORDEM_FATORES.length - 1) return ETAPA.RESUMO;
  return ORDEM_FATORES[idx + 1];
}

/**
 * Dado o fator atual, retorna o anterior.
 * Se for o primeiro, retorna VISTORIA_INICIO.
 */
export function etapaAnterior(atual) {
  const idx = ORDEM_FATORES.indexOf(atual);
  if (idx <= 0) return ETAPA.VISTORIA_INICIO;
  return ORDEM_FATORES[idx - 1];
}

/**
 * Retorna a configuração completa (código, título, pasta, NBR) de uma etapa.
 */
export function getFatorConfig(etapa) {
  return FATORES_CONFIG[etapa] || null;
}

/**
 * Lista os 6 fatores na ordem canônica, já com config.
 */
export function listarFatores() {
  return ORDEM_FATORES.map(etapa => ({
    etapa,
    ...FATORES_CONFIG[etapa],
  }));
}